import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Plus, Edit2, Trash2, Search, X, Sparkles } from "lucide-react";
import { api, formatPrice } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";
import RawgImportModal from "./RawgImportModal";

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [rawgOpen, setRawgOpen] = useState(false);

  const load = () => {
    api.get("/products?limit=200").then((r) => setProducts(r.data.items));
  };

  useEffect(() => { load(); api.get("/categories").then((r) => setCategories(r.data)); }, []);

  const filtered = products.filter((p) => p.title.toLowerCase().includes(search.toLowerCase()));

  const onDelete = async (p) => {
    if (!window.confirm(`Delete ${p.title}?`)) return;
    await api.delete(`/products/${p.id}`);
    toast.success("Deleted");
    load();
  };

  const blank = {
    title: "", description: "", short_description: "", category: categories[0]?.name || "PlayStation",
    platform: "PC", price: 0, discount_percent: 0, cover_image: "", rating: 4.5, rating_count: 0,
    stock: 999, featured: false, trending: false, flash_deal: false, new_release: false, top_seller: false,
    instant_delivery: true, type: "game", tags: [],
  };

  return (
    <div className="space-y-6" data-testid="admin-products">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="font-display text-2xl font-bold text-white">Produtos</h1>
        <div className="flex items-center gap-2">
          <Button onClick={() => setRawgOpen(true)} variant="outline" className="border-[#A855F7]/40 text-[#C084FC] hover:bg-[#A855F7]/10 bg-[#A855F7]/5" data-testid="rawg-import-btn">
            <Sparkles className="w-4 h-4 mr-1.5" /> Import from RAWG
          </Button>
          <Button onClick={() => { setEditing(blank); setOpen(true); }} className="bg-[#A855F7] hover:bg-[#9333EA]" data-testid="new-product-btn">
            <Plus className="w-4 h-4 mr-1.5" /> Novo Produto
          </Button>
        </div>
      </div>

      <div className="rounded-2xl bg-[#171723] border border-white/5 p-4">
        <div className="relative max-w-sm mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar..." className="w-full pl-10 pr-3 h-10 rounded-lg bg-white/5 border border-white/10 text-sm text-white" />
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs text-slate-500">
              <tr>
                <th className="text-left px-2 py-2">Produto</th>
                <th className="text-left px-2 py-2">Categoria</th>
                <th className="text-left px-2 py-2">Preço</th>
                <th className="text-left px-2 py-2">Desc.</th>
                <th className="text-left px-2 py-2">Estoque</th>
                <th className="px-2 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((p) => (
                <tr key={p.id} className="border-t border-white/5">
                  <td className="px-2 py-3"><div className="flex items-center gap-3"><img src={p.cover_image} alt={p.title} className="w-10 h-10 rounded-lg object-cover" /><div className="font-medium text-white truncate max-w-[180px]">{p.title}</div></div></td>
                  <td className="px-2 py-3 text-slate-300">{p.category}</td>
                  <td className="px-2 py-3 text-white font-semibold">{formatPrice(p.price)}</td>
                  <td className="px-2 py-3 text-slate-300">{p.discount_percent}%</td>
                  <td className="px-2 py-3 text-slate-300">{p.stock}</td>
                  <td className="px-2 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => { setEditing({ ...p, tags: p.tags || [] }); setOpen(true); }} className="p-1.5 rounded-lg hover:bg-white/5 text-slate-400 hover:text-white" data-testid={`edit-${p.id}`}><Edit2 className="w-4 h-4" /></button>
                      <button onClick={() => onDelete(p)} className="p-1.5 rounded-lg hover:bg-red-500/10 text-slate-400 hover:text-red-400" data-testid={`del-${p.id}`}><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {open && editing && (
        <ProductModal product={editing} categories={categories} onClose={() => setOpen(false)} onSaved={() => { setOpen(false); load(); }} />
      )}

      <RawgImportModal
        open={rawgOpen}
        onClose={() => setRawgOpen(false)}
        onImported={() => load()}
        categories={categories}
      />
    </div>
  );
}

function ProductModal({ product, categories, onClose, onSaved }) {
  const [form, setForm] = useState(product);
  const isNew = !product.id;
  const save = async () => {
    const payload = { ...form, price: parseFloat(form.price) || 0, discount_percent: parseFloat(form.discount_percent) || 0, stock: parseInt(form.stock) || 0 };
    try {
      if (isNew) await api.post("/products", payload);
      else await api.put(`/products/${form.id}`, payload);
      toast.success("Saved");
      onSaved();
    } catch (e) { toast.error("Failed to save"); }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/70 backdrop-blur p-4" onClick={onClose}>
      <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="w-full max-w-2xl rounded-3xl bg-[#171723] border border-white/10 p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="product-modal">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-xl font-bold text-white">{isNew ? "Novo Produto" : "Editar Produto"}</h3>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-white/5 text-slate-400"><X className="w-4 h-4" /></button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Input label="Title" value={form.title} onChange={(v) => setForm({ ...form, title: v })} />
          <Select label="Category" value={form.category} onChange={(v) => setForm({ ...form, category: v })} options={categories.map((c) => c.name)} />
          <Input label="Platform" value={form.platform} onChange={(v) => setForm({ ...form, platform: v })} />
          <Select label="Type" value={form.type} onChange={(v) => setForm({ ...form, type: v })} options={["game", "gift_card", "subscription", "premium_account"]} />
          <Input label="Price" type="number" value={form.price} onChange={(v) => setForm({ ...form, price: v })} />
          <Input label="Discount %" type="number" value={form.discount_percent} onChange={(v) => setForm({ ...form, discount_percent: v })} />
          <Input label="Stock" type="number" value={form.stock} onChange={(v) => setForm({ ...form, stock: v })} />
          <Input label="Cover Image URL" value={form.cover_image} onChange={(v) => setForm({ ...form, cover_image: v })} />
          <div className="sm:col-span-2">
            <label className="text-xs text-slate-400 uppercase font-semibold mb-1 block">Description</label>
            <textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full p-3 rounded-lg bg-white/5 border border-white/10 text-sm text-white" />
          </div>
          <div className="sm:col-span-2 flex flex-wrap gap-3">
            {["featured", "trending", "flash_deal", "new_release", "top_seller", "instant_delivery"].map((k) => (
              <label key={k} className="flex items-center gap-2 text-sm text-slate-300">
                <input type="checkbox" checked={!!form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.checked })} className="accent-[#A855F7]" />
                {k.replace("_", " ")}
              </label>
            ))}
          </div>
        </div>
        <div className="flex gap-3 mt-5">
          <Button onClick={save} className="bg-[#A855F7] hover:bg-[#9333EA] flex-1" data-testid="save-product">{isNew ? "Create" : "Save"}</Button>
          <Button variant="outline" onClick={onClose} className="border-white/10 text-slate-300 hover:bg-white/5">Cancel</Button>
        </div>
      </motion.div>
    </div>
  );
}

function Input({ label, value, onChange, type = "text" }) {
  return (
    <label className="block">
      <div className="text-xs text-slate-400 uppercase font-semibold mb-1">{label}</div>
      <input type={type} value={value || ""} onChange={(e) => onChange(e.target.value)} className="w-full h-10 px-3 rounded-lg bg-white/5 border border-white/10 text-sm text-white focus:border-[#A855F7] focus:outline-none" />
    </label>
  );
}

function Select({ label, value, onChange, options }) {
  return (
    <label className="block">
      <div className="text-xs text-slate-400 uppercase font-semibold mb-1">{label}</div>
      <select value={value} onChange={(e) => onChange(e.target.value)} className="w-full h-10 px-3 rounded-lg bg-white/5 border border-white/10 text-sm text-white focus:border-[#A855F7] focus:outline-none">
        {options.map((o) => <option key={o} value={o} className="bg-[#171723]">{o}</option>)}
      </select>
    </label>
  );
}
